import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { pb } from '@/lib/pb';
import { COLLECTIONS, type Post, type Category } from '@/lib/types';
import { Btn, Icon, fmtDate } from '@/ui';
import { PageHeader, Card, Spinner, useToast, pbError } from './ui';

export function BlogList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [posts, setPosts] = React.useState<Post[] | null>(null);
  const [cats, setCats] = React.useState<Category[]>([]);
  const [search, setSearch] = React.useState('');
  const [statusF, setStatusF] = React.useState('');
  const [catF, setCatF] = React.useState('');

  const reload = React.useCallback(async () => {
    const filters: string[] = [];
    if (search.trim()) filters.push(`title ~ ${JSON.stringify(search.trim())}`);
    if (statusF) filters.push(`status = ${JSON.stringify(statusF)}`);
    if (catF) filters.push(`category = ${JSON.stringify(catF)}`);
    try {
      const res = await pb.collection(COLLECTIONS.posts).getList<Post>(1, 200, {
        sort: '-created', expand: 'category',
        filter: filters.join(' && '),
      });
      setPosts(res.items);
    } catch (e) { toast.show(pbError(e), 'error'); setPosts([]); }
  }, [search, statusF, catF, toast]);

  React.useEffect(() => { void reload(); }, [reload]);
  React.useEffect(() => {
    void pb.collection(COLLECTIONS.categories).getFullList<Category>({ sort: 'name' }).then(setCats).catch(() => setCats([]));
  }, []);

  const remove = async (p: Post) => {
    if (!confirm(`Beitrag „${p.title}" löschen?`)) return;
    try {
      await pb.collection(COLLECTIONS.posts).delete(p.id);
      toast.show('Beitrag gelöscht.', 'success');
      await reload();
    } catch (e) { toast.show(pbError(e), 'error'); }
  };

  const selStyle: React.CSSProperties = { fontSize: 14, color: 'var(--text-100)', background: 'var(--surface-card)', padding: '9px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', cursor: 'pointer' };

  return (
    <div>
      <PageHeader
        title="Blog"
        subtitle="Beiträge erstellen und verwalten."
        actions={<Btn variant="primary" size="sm" iconLeft="plus" onClick={() => navigate('/admin/blog/new')}>Neuer Beitrag</Btn>}
      />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-200)', display: 'inline-flex' }}><Icon name="search" size={16} /></span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Titel suchen…"
            style={{ ...selStyle, width: '100%', boxSizing: 'border-box', paddingLeft: 36, cursor: 'text' }} />
        </div>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} style={selStyle}>
          <option value="">Alle Status</option>
          <option value="draft">Entwurf</option>
          <option value="published">Veröffentlicht</option>
        </select>
        <select value={catF} onChange={(e) => setCatF(e.target.value)} style={selStyle}>
          <option value="">Alle Kategorien</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {posts === null ? <Spinner /> : posts.length === 0 ? (
          <div style={{ padding: 28, color: 'var(--text-200)', fontSize: 14, textAlign: 'center' }}>Keine Beiträge gefunden.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-200)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ padding: '12px 18px' }}>Titel</th>
                <th style={{ padding: '12px 18px' }}>Kategorie</th>
                <th style={{ padding: '12px 18px' }}>Status</th>
                <th style={{ padding: '12px 18px' }}>Datum</th>
                <th style={{ padding: '12px 18px', textAlign: 'right' }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border-subtle)', color: 'var(--text-100)', cursor: 'pointer' }} onClick={() => navigate(`/admin/blog/${p.id}`)}>
                  <td style={{ padding: '12px 18px', fontWeight: 500 }}>{p.title}</td>
                  <td style={{ padding: '12px 18px', color: 'var(--text-200)' }}>{p.expand?.category?.name ?? '—'}</td>
                  <td style={{ padding: '12px 18px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 9px', borderRadius: 'var(--radius-pill)', background: p.status === 'published' ? 'rgba(48,163,108,0.14)' : 'var(--surface-sunken)', color: p.status === 'published' ? '#30a46c' : 'var(--text-200)' }}>
                      {p.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 18px', color: 'var(--text-200)', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{fmtDate(p.published_at || p.created)}</td>
                  <td style={{ padding: '10px 18px', textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => navigate(`/admin/blog/${p.id}`)} title="Bearbeiten" style={iconBtn('var(--text-200)')}><Icon name="pencil" size={16} /></button>
                    <button onClick={() => remove(p)} title="Löschen" style={iconBtn('#e5484d')}><Icon name="trash-2" size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function iconBtn(color: string): React.CSSProperties {
  return { background: 'none', border: 'none', cursor: 'pointer', padding: 7, marginLeft: 2, borderRadius: 'var(--radius-sm)', color, display: 'inline-flex', verticalAlign: 'middle' };
}
